import * as React from 'react';
import type { FetchResult, MutationFn, QueryResult } from 'react-apollo';
import { Mutation, Query } from 'react-apollo';

import type { ApolloQueryResult } from 'apollo-client';

import createCommentMutation from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/CreateCommentMutation.graphql';
import createCommentUsageMutation from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/CreateCommentUsageMutation.graphql';
import deleteCommentUsagesMutation from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/DeleteCommentUsagesMutation.graphql';
import getCommentsQuery from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/GetCommentsQuery.graphql';
import type {
  CmlContentInput,
  CommentFilter,
  CreateCommentMutation,
  CreateCommentMutationVariables,
  CreateCommentUsageMutation,
  CreateCommentUsageMutationVariables,
  DeleteCommentUsagesMutation,
  DeleteCommentUsagesMutationVariables,
  GetCommentsQuery,
  GetCommentsQueryVariables,
} from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/types';
import type { FetchStatus } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';

const GQL_CONTEXT = { clientName: 'gatewayGql' };

type QueryWrapperProps = {
  variables: GetCommentsQueryVariables;
  children: (result: QueryResult<GetCommentsQuery, GetCommentsQueryVariables>) => React.ReactElement | null;
  skip?: boolean;
};

const ReusableCommentsQueryWrapper = ({ variables, children, skip }: QueryWrapperProps) => {
  return (
    <Query<GetCommentsQuery, GetCommentsQueryVariables>
      query={getCommentsQuery}
      variables={variables}
      context={GQL_CONTEXT}
      skip={skip}
    >
      {children}
    </Query>
  );
};

type ReusableCommentsMutationInjectedProps = {
  createComment: MutationFn<CreateCommentMutation, CreateCommentMutationVariables>;
  createCommentUsage: MutationFn<CreateCommentUsageMutation, CreateCommentUsageMutationVariables>;
  deleteCommentUsages: MutationFn<DeleteCommentUsagesMutation, DeleteCommentUsagesMutationVariables>;
  createCommentStatus: FetchStatus;
  createCommentUsageStatus: FetchStatus;
  deleteCommentUsagesStatus: FetchStatus;
};

type MutationWrapperProps = {
  children: (mutations: ReusableCommentsMutationInjectedProps) => React.ReactElement | null;
};
const ReusableCommentsMutationWrapper = ({ children }: MutationWrapperProps) => {
  return (
    <Mutation<CreateCommentMutation, CreateCommentMutationVariables>
      mutation={createCommentMutation}
      context={GQL_CONTEXT}
    >
      {(createComment, createCommentResult) => {
        return (
          <Mutation<CreateCommentUsageMutation, CreateCommentUsageMutationVariables>
            mutation={createCommentUsageMutation}
            context={GQL_CONTEXT}
          >
            {(createCommentUsage, createCommentUsageResult) => {
              return (
                <Mutation<DeleteCommentUsagesMutation, DeleteCommentUsagesMutationVariables>
                  mutation={deleteCommentUsagesMutation}
                  context={GQL_CONTEXT}
                >
                  {(deleteCommentUsages, deleteCommentResult) => {
                    const createCommentStatus: FetchStatus = {
                      loading: createCommentResult.loading,
                      error: createCommentResult.error,
                    };
                    const createCommentUsageStatus: FetchStatus = {
                      loading: createCommentUsageResult.loading,
                      error: createCommentUsageResult.error,
                    };
                    const deleteCommentUsagesStatus: FetchStatus = {
                      loading: deleteCommentResult.loading,
                      error: deleteCommentResult.error,
                    };

                    const mutationsProps: ReusableCommentsMutationInjectedProps = {
                      createComment,
                      createCommentUsage,
                      deleteCommentUsages,
                      createCommentStatus,
                      createCommentUsageStatus,
                      deleteCommentUsagesStatus,
                    };
                    return children(mutationsProps);
                  }}
                </Mutation>
              );
            }}
          </Mutation>
        );
      }}
    </Mutation>
  );
};

export type ProvidedActionsType = {
  refetchComments: (filterCml?: CmlContentInput) => Promise<ApolloQueryResult<GetCommentsQuery>>;
  createComment: (commentContentInput: CmlContentInput) => Promise<void | FetchResult<CreateCommentMutation>>;
  createCommentUsage: (commentId: string) => Promise<void | FetchResult<CreateCommentUsageMutation>>;
  deleteCommentUsages: (commentId: string) => Promise<void | FetchResult<DeleteCommentUsagesMutation>>;
};

export type DataProviderProps = {
  assignmentVersion: number;
  assignmentId: string;
  reviewPartId: string;
  children: (props: { actions: ProvidedActionsType }) => React.ReactElement | null;
};

const MAX_COMMENTS_FETCHED = 100;

export const DataProvider = ({ assignmentVersion, assignmentId, reviewPartId, children }: DataProviderProps) => {
  const initialQueryVariables: GetCommentsQueryVariables = {
    commentFilter: {
      assignmentId,
      reviewPartId,
    },
    first: MAX_COMMENTS_FETCHED,
  };
  const mutationVariablesData = {
    assignmentId,
    reviewPartId,
    assignmentVersion,
  };

  return (
    <ReusableCommentsQueryWrapper variables={initialQueryVariables}>
      {(getListResponse) => {
        return (
          <ReusableCommentsMutationWrapper>
            {(mutations) => {
              const createComment = (commentContentInput: CmlContentInput) => {
                return mutations.createComment({
                  variables: {
                    input: {
                      commentContentInput,
                      ...mutationVariablesData,
                    },
                  },
                });
              };
              const deleteCommentUsages = (commentId: string) => {
                return mutations.deleteCommentUsages({
                  variables: {
                    input: {
                      commentId,
                      assignmentId: mutationVariablesData.assignmentId,
                      reviewPartId: mutationVariablesData.reviewPartId,
                    },
                  },
                });
              };

              const refetchComments = (filterCml?: CmlContentInput) => {
                const variables: GetCommentsQueryVariables = { ...initialQueryVariables };
                if (filterCml) {
                  (variables.commentFilter as CommentFilter).partialCommentContent = filterCml;
                }
                return getListResponse.refetch(variables);
              };

              const createCommentUsage = (commentId: string) => {
                return mutations.createCommentUsage({
                  variables: {
                    input: {
                      commentId,
                      ...mutationVariablesData,
                    },
                  },
                });
              };
              return children({
                actions: {
                  createComment,
                  createCommentUsage,
                  deleteCommentUsages,
                  refetchComments,
                },
              });
            }}
          </ReusableCommentsMutationWrapper>
        );
      }}
    </ReusableCommentsQueryWrapper>
  );
};
